import React, {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {cdnImageWidth} from "./utils.tsx";

interface MiceLightboxProps {
    images: string[];
    startIndex: number;
    title: string;
    onClose: () => void;
}

const FOCUS_RING = 'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white';

const Chevron = ({dir}: { dir: 'left' | 'right' }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={dir === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'}/>
    </svg>
);

// Galería a pantalla completa de una ficha. Va en un portal fuera del carrusel: dentro de la slide
// quedaba bajo las flechas del visor y el foco desplazaba los contenedores overflow:hidden de Swiper.
// Si el visor está en pantalla completa se monta en ese elemento, que es lo único que se ve.
// z-index por encima de .global-progress-bar (9999 en App.css).
export const MiceLightbox: React.FC<MiceLightboxProps> = ({images, startIndex, title, onClose}) => {
    const [index, setIndex] = useState(startIndex);
    const closeRef = useRef<HTMLButtonElement>(null);
    const thumbsRef = useRef<HTMLDivElement>(null);
    const touchStartX = useRef<number | null>(null);
    const count = images.length;

    const go = (delta: number) => setIndex(i => (i + delta + count) % count);

    // Teclado en fase de captura sobre window: así las flechas no llegan al módulo Keyboard de Swiper
    // (que escucha en document) y no cambian de página del catálogo mientras la galería está abierta.
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowLeft') go(-1);
            else if (e.key === 'ArrowRight') go(1);
            else return;
            e.preventDefault();
            e.stopPropagation();
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [count, onClose]); // eslint-disable-line react-hooks/exhaustive-deps

    // Foco al botón de cerrar al abrir, y de vuelta al elemento que la abrió al cerrar.
    // preventScroll: sin él, el navegador desplaza los contenedores del carrusel para "mostrar" el elemento.
    useEffect(() => {
        const opener = document.activeElement as HTMLElement | null;
        closeRef.current?.focus({preventScroll: true});
        return () => opener?.focus?.({preventScroll: true});
    }, []);

    // Mantiene visible la miniatura activa. A mano y no con scrollIntoView, que también
    // desplazaría los contenedores del carrusel que hay por encima.
    useEffect(() => {
        const strip = thumbsRef.current;
        const thumb = strip?.querySelector<HTMLElement>(`[data-index="${index}"]`);
        if (!strip || !thumb) return;
        strip.scrollTo({left: thumb.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2, behavior: 'smooth'});
    }, [index]);

    // Precarga las fotos vecinas para que el paso sea instantáneo
    useEffect(() => {
        [index - 1, index + 1].forEach(i => {
            const file = images[(i + count) % count];
            if (file) new Image().src = cdnImageWidth(file, 1800) ?? file;
        });
    }, [index, images, count]);

    const onTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0].clientX; };
    const onTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
        touchStartX.current = null;
    };

    return createPortal(
        <div role="dialog" aria-modal="true" aria-label={`Galería de ${title}`}
             className="fixed inset-0 z-[10000] flex flex-col bg-[#141311] text-white">
            <div className="flex items-center justify-between gap-4 px-4 md:px-8 h-16 flex-shrink-0">
                <div className="min-w-0 text-sm">
                    <span className="font-semibold">{index + 1} / {count}</span>
                    <span className="hidden md:inline text-white/70"> · {title}</span>
                </div>
                <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar galería"
                        className={`size-11 p-0 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-white border-0 cursor-pointer ${FOCUS_RING}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                         strokeLinecap="round" aria-hidden="true">
                        <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                </button>
            </div>

            <div className="relative flex-1 min-h-0 flex items-center justify-center px-4 md:px-24"
                 onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                <img key={images[index]} src={cdnImageWidth(images[index], 1800)} alt={`${title}, foto ${index + 1} de ${count}`}
                     className="max-w-full max-h-full object-contain rounded-lg animate-fadeIn select-none" draggable={false}/>
                {count > 1 && (
                    <>
                        <button type="button" onClick={() => go(-1)} aria-label="Foto anterior"
                                className={`hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 size-12 p-0 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white border-0 cursor-pointer ${FOCUS_RING}`}>
                            <Chevron dir="left"/>
                        </button>
                        <button type="button" onClick={() => go(1)} aria-label="Foto siguiente"
                                className={`hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 size-12 p-0 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white border-0 cursor-pointer ${FOCUS_RING}`}>
                            <Chevron dir="right"/>
                        </button>
                    </>
                )}
            </div>

            {count > 1 && (
                <div ref={thumbsRef} className="relative flex gap-2 px-4 md:px-8 py-4 overflow-x-auto hide-scrollbar flex-shrink-0 md:justify-center">
                    {images.map((file, i) => (
                        <button key={file} type="button" data-index={i} onClick={() => setIndex(i)}
                                aria-label={`Ver foto ${i + 1}`} aria-current={i === index ? 'true' : undefined}
                                className={`flex-shrink-0 w-16 h-12 md:w-20 md:h-14 p-0 rounded-md overflow-hidden border-2 cursor-pointer bg-transparent ${i === index ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'} ${FOCUS_RING}`}>
                            <img src={cdnImageWidth(file, 160)} alt="" loading="lazy" className="w-full h-full object-cover"/>
                        </button>
                    ))}
                </div>
            )}
        </div>,
        document.fullscreenElement ?? document.body
    );
};
